import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";

type Problem = {
  id: string;
  payload: { display?: string; code?: string; onset_date?: string | null };
};

/**
 * What this patient lives with.
 *
 * The first question a doctor asks when they open a chart, so it answers
 * before the visit history rather than after it.
 *
 * It stays empty until somebody curates it, and that is the design. A list
 * that fills itself becomes a list nobody reads, and an unread problem list is
 * worse than none because it looks like an answer (ADR-020 point 2).
 *
 * Nothing here counts what the caller cannot see. A restricted problem is
 * absent from the response, and a count of hidden items would leak the thing
 * the tier protects (CLN-F-125).
 */
export function ProblemList({ patientId, isDoctor }: { patientId: string; isDoctor: boolean }) {
  const [rows, setRows] = useState<Problem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await api.GET("/portal/patients/{id}/problems", {
      params: { path: { id: patientId }, header: clinicHeader() },
    });
    setLoading(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows((data?.items ?? []) as Problem[]);
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (row: Problem) => {
    setBusy(row.id);
    const { error: failed } = await api.POST("/portal/conditions/{id}/category", {
      params: { path: { id: row.id }, header: clinicHeader() },
      body: { category: "encounter_diagnosis" },
    });
    setBusy(null);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
  };

  if (loading) return null;

  return (
    <section className="problems">
      <h2>Problem list</h2>
      <ErrorNote message={error} />

      {rows.length === 0 && (
        <p className="empty">
          Nothing on the problem list. A diagnosis joins it when a doctor puts it there.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="problems-list">
          {rows.map((row) => (
            <li key={row.id}>
              <span className="grow">
                <strong>{row.payload?.display}</strong>
                {row.payload?.code && <span className="muted"> · {row.payload.code}</span>}
                {row.payload?.onset_date && (
                  <span className="muted"> · since {row.payload.onset_date}</span>
                )}
              </span>
              {isDoctor && (
                <button
                  type="button"
                  className="linklike"
                  disabled={busy === row.id}
                  onClick={() => remove(row)}
                >
                  Take off the list
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
